import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { enableProdMode, provideZoneChangeDetection } from '@angular/core';
import { MAT_DIALOG_DEFAULT_OPTIONS } from '@angular/material/dialog';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, withHashLocation } from '@angular/router';
import { MessageBoxService } from '@fundamental-ngx/core';
import { provideTheming, themingInitializer } from '@fundamental-ngx/core/theming';
import { provideEffects } from '@ngrx/effects';
import { provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import { provideTranslateService } from '@ngx-translate/core';
import * as echarts from 'echarts';
import { NgxEchartsModule } from 'ngx-echarts';

import { AppComponent } from './app/app.component';
import { routes } from './app/app.routes';
import { en } from './app/i18n/en';
import { IframeMessageService } from './app/init-services/iframe-message.service';
import { effects, metaReducers, reducers } from './app/ngrx/provide-store';
import { EnvironmentService } from './app/shared/environment.service';
import {
  FEATURE_TRACKER_INIT_MATOMO,
  FEATURE_TRACKER_MATOMO_APPLICATION_ID,
  FEATURE_TRACKER_MATOMO_ENDPOINT,
  FEATURE_TRACKER_MATOMO_IS_IFRAME,
} from './app/shared/feature-usage-tracker/matomo.service';
import { CumulusAuthInterceptorService } from './cumulus-auth-interceptor/cumulus-auth-interceptor.service';
import { environment } from './environments/environment';

if (environment.production) {
  enableProdMode();
}

bootstrapApplication(AppComponent, {
  providers: [
    provideZoneChangeDetection(),
    provideAnimations(),
    provideHttpClient(withInterceptorsFromDi()),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: CumulusAuthInterceptorService,
      multi: true,
    },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: { enterAnimationDuration: 0, exitAnimationDuration: 0 },
    },
    provideTheming({ defaultTheme: 'sap_horizon', changeThemeOnQueryParamChange: false }),
    themingInitializer(),
    provideTranslateService({ fallbackLang: 'en' }),
    {
      provide: 'TRANSLATE_EN',
      useValue: en,
    },
    {
      provide: FEATURE_TRACKER_MATOMO_ENDPOINT,
      useFactory: (environmentService: EnvironmentService) => {
        return environmentService.getMatomoUrl();
      },
      deps: [EnvironmentService],
    },
    {
      provide: FEATURE_TRACKER_MATOMO_APPLICATION_ID,
      useFactory: (environmentService: EnvironmentService) => {
        return environmentService.getMatomoApplicationId();
      },
      deps: [EnvironmentService],
    },
    {
      provide: FEATURE_TRACKER_INIT_MATOMO,
      useFactory: (environmentService: EnvironmentService) => {
        return environmentService.getMatomoInitFlag();
      },
      deps: [EnvironmentService],
    },
    {
      provide: FEATURE_TRACKER_MATOMO_IS_IFRAME,
      useValue: true,
    },
    MessageBoxService,
    IframeMessageService,
    provideRouter(routes, withHashLocation()),
    provideStore(reducers, {
      metaReducers,
      runtimeChecks: {
        strictStateImmutability: true,
        strictActionImmutability: true,
        strictActionWithinNgZone: true,
        strictActionTypeUniqueness: false,
        strictStateSerializability: false,
        strictActionSerializability: false,
      },
    }),
    provideEffects(effects),
    provideStoreDevtools({
      logOnly: environment.production,
    }),
    NgxEchartsModule.forRoot({ echarts }).providers ?? [],
  ],
}).catch((err) => console.error(err));
